import { BarPlot, ChartsGrid, ChartsTooltip, ChartsXAxis, ChartsYAxis, LinePlot, MarkPlot, ResponsiveChartContainer } from '@mui/x-charts'
import { FunctionComponent, useMemo } from 'react'
import { IReductionScenarioSeries, IReductionTargetGraphData } from '../../../shared/services/reduction-target/reduction-target.model'
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { IColorPalatte } from '../../../modules/public-reports/shared-services/public-reports.model';
import { getMixedColorPalette } from '../../../shared/utils/colors-utils';

type LegendItem = { label: string; color: string; type: 'bar' | 'line'; dashed?: boolean }

function LegendSwatch({ item }: { item: LegendItem }) {
  if (item.type === 'bar') {
    return <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: item.color, flexShrink: 0 }} />
  }
  return (
    <Box sx={{ width: 20, height: 0, borderTop: `2px ${item.dashed ? 'dashed' : 'solid'} ${item.color}`, flexShrink: 0 }} />
  )
}

export const ReductionGraph: FunctionComponent<{
  graphData: IReductionTargetGraphData[],
  includeCurrent?: boolean,
  colorPalatte?: IColorPalatte,
  leftMarginPx?: number,
  chartHeight?: number,
  compact?: boolean,
  scenarioSeries?: IReductionScenarioSeries[],
  annualGrowthPercentage?: number | null,
}> = ({ graphData, includeCurrent = false, colorPalatte, leftMarginPx, chartHeight, compact = false, scenarioSeries, annualGrowthPercentage }) => {

  const lineData = graphData.map(item => item.targetValue);
  const barActualData = graphData.map(item => item.actualValue);
  const barCurrentData = graphData.map(item => item.currentValue);

  // Index of the most recent year with a reported actual — used to anchor the
  // projection/scenario lines so they visibly step down from the actual bar
  // rather than floating in from the next year.
  const lastActualIndex = useMemo(() => {
    let idx = -1;
    graphData.forEach((item, i) => { if (item.actualValue != null) idx = i; });
    return idx;
  }, [graphData]);
  const actualAtLast = lastActualIndex >= 0 ? graphData[lastActualIndex].actualValue ?? null : null;

  const anchorToActual = (arr: (number | null)[]): (number | null)[] => {
    if (lastActualIndex < 0 || actualAtLast == null) return arr;
    const copy = arr.slice();
    if (copy[lastActualIndex] == null) copy[lastActualIndex] = actualAtLast;
    return copy;
  };

  const projectedData = useMemo(
    () => anchorToActual(graphData.map(item => item.projectedValue ?? null)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graphData, lastActualIndex, actualAtLast]
  );
  const hasProjections = graphData.some(item => item.projectedValue != null);

  const theme = useTheme();
  // Distinct companion hues for scenario lines, dropping the first (primary) entry so
  // scenarios don't clash with the green target line.
  const scenarioColors = useMemo(
    () => getMixedColorPalette(theme.palette.primary.main).slice(1),
    [theme.palette.primary.main]
  );
  const primaryMain = colorPalatte ? colorPalatte.main : theme.palette.primary.main;
  const primaryLight = colorPalatte ? colorPalatte.light : theme.palette.primary.light;
  const primaryDark = colorPalatte ? colorPalatte.dark : theme.palette.primary.dark;
  const projectedColor = theme.palette.warning?.main || '#ed6c02';
  const leftMargin = leftMarginPx ?? 90;
  const resolvedChartHeight = chartHeight ?? 400;
  const chartMargin = compact
    ? { left: leftMargin, top: 8, right: 8, bottom: 20 }
    : { left: leftMargin, top: 20, right: 20, bottom: 30 };

  const visibleScenarios = scenarioSeries ?? [];

  const buildSeries = (includeCurrentBars: boolean) => {
    const series: any[] = [
      {
        type: 'bar',
        data: barActualData,
        color: primaryDark,
        label: 'Actual',
        stack: 'total'
      },
    ];

    if (includeCurrentBars) {
      series.push({
        type: 'bar',
        data: barCurrentData,
        color: primaryLight,
        label: 'In progress (unlocked)',
        stack: 'total'
      });
    }

    series.push({
      type: 'line',
      id: 'r-target',
      data: lineData,
      color: primaryMain,
      label: 'Target',
    });

    if (hasProjections) {
      series.push({
        type: 'line',
        id: 'r-projected',
        data: projectedData,
        color: projectedColor,
        label: 'Projected (planned actions)',
      });
    }

    visibleScenarios.forEach((scenario, index) => {
      series.push({
        type: 'line',
        id: `r-scenario-${index}`,
        data: anchorToActual(scenario.data),
        color: scenarioColors[index % scenarioColors.length],
        label: scenario.name,
      });
    });

    return series;
  };

  // Dashed styling distinguishes hypothetical/reference lines (target + scenarios)
  // from the solid committed-plan (projected) line and the solid actual bars.
  const lineDashSx = useMemo(() => {
    const sx: Record<string, any> = {
      '& .MuiLineElement-series-r-target': { strokeDasharray: '6 5' },
    };
    visibleScenarios.forEach((_, index) => {
      sx[`& .MuiLineElement-series-r-scenario-${index}`] = { strokeDasharray: '5 4' };
    });
    return sx;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleScenarios.length]);

  const legendItems: LegendItem[] = [
    { label: 'Actual', color: primaryDark, type: 'bar' },
    ...(includeCurrent ? [{ label: 'In progress (unlocked)', color: primaryLight, type: 'bar' as const }] : []),
    { label: 'Target', color: primaryMain, type: 'line', dashed: true },
    ...(hasProjections ? [{ label: 'Projected (planned actions)', color: projectedColor, type: 'line' as const }] : []),
    ...visibleScenarios.map((s, i) => ({ label: s.name, color: scenarioColors[i % scenarioColors.length], type: 'line' as const, dashed: true })),
  ];

  return (
    <Box>
      <Typography variant={compact ? 'body2' : 'subtitle1'} sx={{ color: 'text.primary', fontWeight: 600, mb: compact ? 0.5 : 0, flexShrink: 0 }}>
        Annual Emissions and Target Comparisons (tCO<sub>2</sub>e)
      </Typography>
      <Box sx={{ height: resolvedChartHeight }}>
        <ResponsiveChartContainer
          height={resolvedChartHeight}
          series={buildSeries(includeCurrent)}
          xAxis={[
            {
              data: graphData.map(item => item.year),
              scaleType: 'band',
              id: 'x-axis-id',
            },
          ]}
          yAxis={[{
            disableLine: true,
          }]}
          margin={chartMargin}
          sx={lineDashSx}
        >
          <ChartsGrid
            horizontal={true}
            vertical={false}
            stroke="#e0e0e0"
            strokeWidth={1}
          />
          <BarPlot borderRadius={5} />
          <LinePlot />
          <MarkPlot />
          <ChartsXAxis />
          <ChartsYAxis />
          <ChartsTooltip />
        </ResponsiveChartContainer>
      </Box>

      <Stack direction='row' flexWrap='wrap' alignItems='center' gap={compact ? 1.5 : 2} sx={{ mt: 1, px: 1 }}>
        {legendItems.map((item) => (
          <Stack key={`${item.type}-${item.label}`} direction='row' alignItems='center' gap={0.75}>
            <LegendSwatch item={item} />
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>{item.label}</Typography>
          </Stack>
        ))}
      </Stack>

      {!compact && (
        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 1, px: 1 }}>
          Bars are reported actuals and “Target” is the trajectory needed to hit your goal.
          “Projected (planned actions)” is your committed plan and drives your coverage metric;
          “Scenario” lines are what-if comparisons only and don’t change your plan.
          {annualGrowthPercentage != null && annualGrowthPercentage !== 0 && (
            <> Projected and scenario lines assume {annualGrowthPercentage}% annual business growth before reductions are applied.</>
          )}
        </Typography>
      )}
    </Box>
  )
}
